/**
 * Auto-categorization rules per payee (`payee_rule` table).
 *
 * Patterns:
 *   - plain text  → case-insensitive "contains" match against the payee.
 *   - `/…/`        → regular expression (case-insensitive). If the regex is
 *                    invalid, the rule is silently ignored (never throws).
 *
 * The highest-priority rule wins; ties use the received order.
 */
import { desc, eq, payee_rule, type PowerSyncDrizzleDB } from "@cashsync/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

export interface PayeeRuleRow {
  id: string;
  /** Pattern to match against the payee (base case-insensitive substring). */
  pattern: string;
  category_id: string;
  priority: number;
}

export interface PayeeRuleDraft {
  pattern: string;
  categoryId: string;
  priority?: number;
}

/** User rules, from highest to lowest priority. */
export function getPayeeRulesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(payee_rule)
    .where(eq(payee_rule.user_id, userId))
    .orderBy(desc(payee_rule.priority));
}

type MatchableRule = Pick<PayeeRuleRow, "pattern" | "category_id" | "priority">;

/** Defensive limit: an absurdly long regex is usually an input error. */
const MAX_PATTERN_LENGTH = 512;

/** Is this a regex-shaped pattern (`/body/`)? */
function regexBody(pattern: string): string | null {
  if (pattern.length >= 3 && pattern.startsWith("/") && pattern.endsWith("/")) {
    return pattern.slice(1, -1);
  }
  return null;
}

/** Does an individual rule match the payee? Never throws. */
export function payeeRuleMatches(pattern: string, payee: string): boolean {
  const trimmed = pattern.trim();
  if (!trimmed || !payee || trimmed.length > MAX_PATTERN_LENGTH) {
    return false;
  }
  const body = regexBody(trimmed);
  if (body !== null) {
    try {
      return new RegExp(body, "iu").test(payee);
    } catch {
      return false;
    }
  }
  return payee.toLowerCase().includes(trimmed.toLowerCase());
}

/**
 * Applies the rules to a payee and returns the suggested `category_id`, or null.
 * Sorts internally by descending priority: it does not depend on input order.
 */
export function applyPayeeRules(rules: readonly MatchableRule[], payee: string): string | null {
  if (!payee.trim()) {
    return null;
  }
  const ordered = rules.toSorted((a, b) => b.priority - a.priority);
  for (const rule of ordered) {
    if (payeeRuleMatches(rule.pattern, payee)) {
      return rule.category_id;
    }
  }
  return null;
}

/**
 * Returns the suggested category for a payee according to the rules (the first
 * one that matches by priority), or null.
 */
export function matchPayeeRule(payee: string, rules: readonly MatchableRule[]): string | null {
  return applyPayeeRules(rules, payee);
}

// ─── History: most-used category for a payee ───────────────────────────────

/** A past transaction (the minimum needed to infer the most-used category). */
export interface PayeeHistoryRow {
  category_id: string | null;
  /** Number of transactions for that payee with that category. */
  uses: number;
}

/**
 * Most-used category in the history for a payee (deterministic, no AI).
 * Takes the already-aggregated rows (payee, category_id, uses) and returns the
 * `category_id` with the highest `uses`. Ties → the first received (the query
 * returns them by `uses DESC`). Ignores rows without a category. Returns null if none.
 */
export function mostUsedCategory(rows: readonly PayeeHistoryRow[]): string | null {
  let bestId: string | null = null;
  let bestUses = 0;
  for (const row of rows) {
    if (row.category_id && row.uses > bestUses) {
      bestId = row.category_id;
      bestUses = row.uses;
    }
  }
  return bestId;
}

/** Where the suggestion comes from: a user rule or the payee history. */
export type PayeeSuggestionSource = "rule" | "history";

export interface PayeeCategorySuggestion {
  categoryId: string;
  source: PayeeSuggestionSource;
}

/**
 * Category suggestion for a payee, combining both DETERMINISTIC signals:
 *   1. user rules (exact/contains/regex match; the highest priority wins);
 *   2. if no rule matches, the most-used category in the payee history.
 * Returns the category and its source, or null if there is nothing to suggest.
 * A rule ALWAYS takes precedence over history (explicit user intent).
 */
export function suggestPayeeCategory(
  payee: string,
  rules: readonly MatchableRule[],
  history: readonly PayeeHistoryRow[],
): PayeeCategorySuggestion | null {
  if (!payee.trim()) {
    return null;
  }
  const ruleMatch = applyPayeeRules(rules, payee);
  if (ruleMatch) {
    return { categoryId: ruleMatch, source: "rule" };
  }
  const historyMatch = mostUsedCategory(history);
  if (historyMatch) {
    return { categoryId: historyMatch, source: "history" };
  }
  return null;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createPayeeRuleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: PayeeRuleDraft,
): Promise<string> {
  const pattern = draft.pattern.trim();
  if (!pattern) {
    throw new Error("The rule needs a pattern");
  }
  const id = generateId();
  const now = nowISO();
  await db.insert(payee_rule).values({
    id,
    user_id: userId,
    pattern,
    category_id: draft.categoryId,
    priority: draft.priority ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updatePayeeRuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ pattern: string; category_id: string; priority: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const set = { ...updates };
  if (set.pattern !== undefined) {
    set.pattern = set.pattern.trim();
  }
  await patchRowVerified(db, payee_rule, payee_rule.id, id, set, options);
}

export async function deletePayeeRuleMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(payee_rule).where(eq(payee_rule.id, id));
}
